import { SupabaseScribeJob } from '@/lib/ai-scribe-job-utils.service';
import envConfig from '@/lib/env-config';
import { supabase } from '@/lib/supabase';
import {
  AppointmentsStateSelector,
  IntegrationStateSelector,
} from '@/store/selectors';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IntegrationApiClient } from '../lib/integration-api-client';
import { AppDispatch } from '../store';
import {
  setAppointmentsData,
  setAppointmentsError,
  setAppointmentsLoading,
} from '../store/slices/dataSlice';
import { ApiAppointmentResponse } from '../types/appointment';
import { useSelectedLocation } from './use-selected-location';

export const useAppointments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const appointments = useSelector(AppointmentsStateSelector.appointmentsData);
  const loading = useSelector(AppointmentsStateSelector.loading);
  const error = useSelector(AppointmentsStateSelector.error);

  const { selectedLocation } = useSelectedLocation();
  const integrationEndpoints = useSelector(IntegrationStateSelector.endpoints);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const fetchAppointments = async () => {
    if (isFetching) {
      return;
    }
    if (!selectedLocation?.locationId) {
      return;
    }
    // Find the get-todays-appointments endpoint
    const appointmentsEndpoint = integrationEndpoints.find(
      (endpoint) =>
        endpoint.action === 'get-todays-appointments' &&
        endpoint.environment === envConfig.env
    );

    if (!appointmentsEndpoint) {
      console.error(
        '❌ get-todays-appointments endpoint not found in integration endpoints'
      );
      dispatch(setAppointmentsError('Appointments endpoint not configured'));
      return;
    }

    dispatch(setAppointmentsLoading(true));
    setIsFetching(true);
    try {
      const result = await IntegrationApiClient.callEndpoint(
        {
          action: 'get-todays-appointments',
          environment: envConfig.env,
          endpoint_url: appointmentsEndpoint.endpoint_url,
          method: appointmentsEndpoint.method,
          description: appointmentsEndpoint.description,
        },
        { locationId: selectedLocation.locationId } // This will be added as query parameter
      );

      if (result.success && result.data) {
        // Cast the response data to ApiAppointmentResponse array
        let appointments = result.data as unknown as ApiAppointmentResponse[];
        const appointmentIds = appointments.map(
          (appointment) => appointment.appointmentId
        );
        if (appointmentIds.length > 0) {
          const scribeJobs = await fetchScribeJobsByAppointmentIds(
            appointmentIds
          );
          if (scribeJobs.length > 0) {
            appointments = appointments.map((appointment) => {
              const scribeJob = scribeJobs.find(
                (scribeJob) =>
                  scribeJob.appointment_id === String(appointment.appointmentId)
              );
              return {
                ...appointment,
                status: scribeJob?.status,
                scribeJobId: scribeJob?.id,
              };
            });
          }
          dispatch(
            setAppointmentsData(JSON.parse(JSON.stringify(appointments)))
          );
        }
      } else {
        console.error('❌ Failed to fetch appointments:', result.error);
        dispatch(
          setAppointmentsError(result.error || 'Failed to fetch appointments')
        );
      }
    } catch (error) {
      console.error('❌ Error fetching appointments:', error);
      dispatch(setAppointmentsError('Failed to fetch appointments'));
    } finally {
      dispatch(setAppointmentsLoading(false));
      setIsFetching(false);
    }
  };

  const fetchScribeJobsByAppointmentIds = async (
    appointmentIds: number[]
  ): Promise<SupabaseScribeJob[]> => {
    if (isFetching) {
      return;
    }
    const locationId = selectedLocation?.locationId;
    if (!locationId) {
      return;
    }
    const { data, error } = await supabase
      .from('ev_scribe_jobs')
      .select('id, status, appointment_id')
      .in('appointment_id', appointmentIds)
      .eq('location_id', locationId);
    if (error) {
      console.error('❌ Error fetching scribe jobs:', error);
      return [];
    } else {
      return data as SupabaseScribeJob[];
    }
  };

  // Only fetch appointments once when component mounts or when selectedLocation changes
  useEffect(() => {
    if (selectedLocation?.locationId && !hasInitialized && !isFetching) {
      fetchAppointments();
      setHasInitialized(true);
    }
  }, [selectedLocation?.locationId, hasInitialized]);

  // Reset initialization when selectedLocation changes
  useEffect(() => {
    if (selectedLocation?.locationId) {
      setHasInitialized(false);
    }
  }, [selectedLocation?.locationId]);

  return {
    appointments,
    loading,
    error,
    refetch: fetchAppointments,
  };
};
